<template>
  <div class="p-5">
    <div class="flex flex-col">
      <p class="font-semibold text-3xl">
        List User Logs
      </p>
    </div>
    <vs-row
      vs-w="12"
      class="filter-container"
    >
      <vs-col vs-w="2">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col
        vs-w="2"
        class="px-1"
      >
        <select-with-search
          v-model="filter.username"
          class="min-w-full"
          placeholder="Select username"
          :options="usernameOptions"
        />
      </vs-col>
      <vs-col
        vs-w="2"
        class="px-1"
      >
        <vs-input
          v-model="filter.pagename"
          placeholder="Search page"
        />
      </vs-col>
      <vs-col
        vs-w="2"
        class="px-1"
      >
        <date-picker
          v-model="filter.start_date"
          type="date"
          format="DD-MMM-YYYY"
          class="min-w-full"
          placeholder="Date start"
        />
      </vs-col>
      <vs-col
        vs-w="2"
        class="px-1"
      >
        <date-picker
          v-model="filter.end_date"
          type="date"
          format="DD-MMM-YYYY"
          class="min-w-full"
          placeholder="Date end"
        />
      </vs-col>
      <vs-col
        vs-w="2"
        class="pl-2"
      >
        <vs-button
          type="filled"
          color="secondary"
          class="w-full self-center font-semibold"
          @click="fetchLogs"
        >
          Filter
        </vs-button>
      </vs-col>
    </vs-row>
    <table-top
      hide-search
      class="mt-5"
      :page-size-start="false"
      @fetch="fetchLogs"
    >
      <template #start>
        <vs-button
          ref="downloadButton"
          type="filled"
          color="secondary"
          class="font-semibold vs-con-loading__container"
          @click="downloadLogs"
        >
          Export Excel
        </vs-button>
      </template>
    </table-top>
    <data-table
      class="mt-5"
      :table-header="logHeaders"
      :table-data="logData"
      :sort-by="filter.sort_by"
      :sort-dir="filter.sort_dir"
      body-height="100vh"
      @sort="sortTable"
    >
      <template #updated_at="{ row }">
        <span>{{ moment(row.updated_at).format('DD-MMM-YYYY HH:mm') }}</span>
      </template>
    </data-table>
    <table-bottom
      class="mt-5"
      @fetch="fetchLogs"
    />
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import DatePicker from '@/components/datePicker'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import downloadUtils from '@/utils/downloadUtils'

export default {
  components: {
    DataTable,
    DatePicker,
    SelectWithSearch,
    TableBottom,
    TableTop
  },
  data() {
    return {
      isDownload: false,
      usernameOptions: [],
      locationOptions: [],
      filter: {
        username: null,
        pagename: null,
        start_date: null,
        end_date: null,
        sort_by: null,
        sort_dir: null
      },
      logHeaders: [
        { key: 'pagename', label: 'Page', sortable: true },
        { key: 'username', label: 'Username', sortable: true },
        { key: 'location', label: 'Location', sortable: true },
        { key: 'updated_at', label: 'Timestamp', sortable: true }
      ],
      logData: []
    }
  },
  computed: {
    ...mapGetters({
      usernames: 'UserLog/usernames',
      locations: 'UserLog/locations',
      logs: 'UserLog/logs',
      logFile: 'UserLog/logFile'
    })
  },
  watch: {
    usernames: {
      handler(val) {
        this.usernameOptions = val.map(item => ({ value: item, text: item }))
      }
    },
    locations: {
      handler(val) {
        this.locationOptions = val.map(item => ({ value: item, text: item }))
      }
    },
    logs: {
      handler(val) {
        this.logData = val
      }
    },
    logFile: {
      handler(val) {
        if (val) {
          downloadUtils(val, `user_logs_${this.moment().format('DD_MMM_YYYY')}`)
          this.$store.dispatch('UserLog/resetLogFile')
        }
      }
    }
  },
  mounted() {
    this.fetchUsernames()
    this.fetchLocations()
  },
  methods: {
    async fetchUsernames() {
      await this.$store.dispatch('UserLog/fetchUsernames')
    },
    async fetchLocations() {
      await this.$store.dispatch('UserLog/fetchLocations')
    },
    async fetchLogs() {
      const customFilter = {
        ...this.filter,
        start_date: this.filter.start_date
          ? this.moment(this.filter.start_date).format('YYYY-MM-DDT00:00:00.000Z')
          : undefined,
        end_date: this.filter.end_date
          ? this.moment(this.filter.end_date).format('YYYY-MM-DDT23:59:59.000Z')
          : undefined
      }
      await this.$store.dispatch('UserLog/fetchLogs', customFilter)
    },
    async downloadLogs() {
      try {
        const customFilter = {
          ...this.filter,
          start_date: this.filter.start_date
            ? this.moment(this.filter.start_date).format('YYYY-MM-DDT00:00:00.000Z')
            : undefined,
          end_date: this.filter.end_date
            ? this.moment(this.filter.end_date).format('YYYY-MM-DDT23:59:59.000Z')
            : undefined
        }
        this.isDownload = true
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)
        await this.$store.dispatch('UserLog/downloadLogs', customFilter)

        this.isDownload = false
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)
      } catch (err) {
        this.isDownload = false
        this.changeButtonState(this.isDownload, this.$refs.downloadButton.$el)
        this.showFailedNotification(this, 'Something went wrong')
      }
    },
    sortTable(sortBy, sortDir) {
      this.filter.sort_by = sortBy
      this.filter.sort_dir = sortDir

      this.fetchLogs()
    },
    changeButtonState(state, container) {
      if (state) {
        this.$vs.loading({
          background: 'secondary',
          color: '#FFF',
          container,
          scale: 0.35
        })
        return
      }

      this.$vs.loading.close(container)
    }
  }
}
</script>

<style lang="scss" scoped>
.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

/deep/.vs-con-input-label {
  @apply w-full;
}
</style>
